import {useEffect, useLayoutEffect, useState} from "react";
import { toast } from "react-toastify";
import {
    GetContractorInfoById,
    UpdateContractorEmployee
} from "../../../../../reduxToolkit/Contractor/ContractorApi";
import {getPhotoPicker, PHOTO_PICKER_MODEL} from '../../../../../components/PhotoPicker/SubComponent/Models/Models';
import {errorMsg} from "../../../../../Apis/functions";
import {CONTRACTOR_EMPLOYEE_DATA, EXTRA_DATA} from '../../../Company/Employees/Model/Models';
import IconPersonDefault from "../../../../../assets/ImagesDefaults/ic-person-v2.svg";
import { GetSingleUserProvider } from "../../../../../reduxToolkit/Providers/providersApi";
import { GetUserRestriction } from './../../../../../reduxToolkit/restrictions/RestrictionApi';
import { useSelector } from "react-redux";
import HandleChange from './../../../../../Helpers/HandleChange';
import { GetHeaders } from './../../../../../reduxToolkit/headers/HeadersApi';

/**
 * @Email : kpozos@ibl.mx
 * @Author: Karen Yunuen Pozos Arias
 * @Job Title: Full Stack Developer
 *
 * @Description:
 * Custom hook for updating contractor employee information.
 * This logic is mainly used in the contractor update employee flow.
 */

export const ContractorUpdateEmployeeApp = (
    {
        t,                          // Translation function
        permission,
        params,                     // Parameters, typically from the URL (e.g., contractor ID)
        dispatch,                   // Redux dispatch function
        setEmptyFields,            // Setter for empty field flag
        isValidEmail,               // Boolean: is email valid
        isValidPhoneNumber,         // Boolean: is phone number valid
        isValidDob,                 // Boolean: is DOB valid
        photoPicker,
        setPhotoPicker,
        setSecretModal,
        setSecretKey,
        extraData,
        extraDataEnabled,
        setExtraData,
        setExtraDataEnabled,
        headersList
    }
) => {
    const [user, setUser] = useState({...CONTRACTOR_EMPLOYEE_DATA});

    const [steps, setSteps] = useState([]);
    const [activeStep, setActiveStep] = useState(0);
    const [modified, setModified] = useState(false);
    const [showUnlinkBtn, setShowUnlinkBtn] = useState(false);

    /**
     * React layout effect hook that runs when `t` or `permission` change.
     *
     * - Updates the `steps` state with the translated step names.
     * - `t('employee_details')` and `t('photo')` are used to get the corresponding translations.
     */
     const  STEP_EMPLOYEE_UPDATE = (extraDataEnabled) => {
        if (extraDataEnabled){
            return [t('details'),t('other_details'), t('photo')];
        }
        return [t('details'), t('photo')];
    }
    useLayoutEffect(() => {
         setSteps(STEP_EMPLOYEE_UPDATE(extraDataEnabled) )
    }, [extraDataEnabled, t, permission]);

    /**
     * Fetches contractor information from the backend using their ID.
     *
     * - Calls `GetContractorInfoById` with the `id` from URL parameters.
     * - If the response is successful (`success`):
     *   - Updates the `user` state with the received data.
     *   - If the contractor has a `selfie`, updates `photoPicker` to show it as a preview.
     * - Calls `GetAllToContractor` to fetch additional contractor-related information.
     */
    const getContractorEmployeeInfo = () => {
        dispatch(GetContractorInfoById(params?.id)).then(
            ({ payload: { data: { data, success } } }) => {
                if (success) {
                    const deviceId = data?.deviceId ?? null;
                    setUser((prevData) => ({
                        ...prevData,
                        ...data,
                    }));
                    setShowUnlinkBtn(!!deviceId);
                    // Reset the picker so the preview always reflects the stored image
                    setPhotoPicker({
                        ...PHOTO_PICKER_MODEL,
                        previewImage: data?.selfie ? `data:image/jpeg;base64,${data?.selfie}` : null,
                        preview: IconPersonDefault
                    });
                    setExtraData(data?.extraData);
                }
            }
        );
    };

    /**
     * Fetch initial data when component loads
     */
    useEffect(() => {
        getContractorEmployeeInfo();
        dispatch(GetHeaders());
        dispatch(GetUserRestriction()).then(
                ({
                    payload: {
                        data: { data, success },
                    },
                }) => {
                    if (success === true) {
                        setExtraDataEnabled( data?.extraDataContractor);
                    }
                },
            );
    }, []);

    /**
     * React effect hook that runs when the `modified` state changes.
     *
     * If `modified` is true, it fetches the latest contractor information
     * by calling `getContractorEmployeeInfo()` and then resets `modified` to false.
     *
     */
    useEffect(() => {
        if(modified){
            getContractorEmployeeInfo();
            setModified(false)
        }
    }, [modified]);

    /**
     * Handles form validation and triggers update if valid
     * @constructor
     */
    const FormHandler = () => {
        if ( !user.isValidEmployee() ||
            !isValidDob ||
            !isValidPhoneNumber ||
            !isValidEmail
        ) {
            toast.warn(t('please_fill_all_fields'));
            setEmptyFields(true);
        } else {
            setEmptyFields(false);
            updateEmployee();
        }
    };

    /**
     * Sends updated user data to API
     */
    const updateEmployee = () => {
        let imageFile = getPhotoPicker(photoPicker);
        dispatch(
            UpdateContractorEmployee({
                data: user.toCreateUpdate(user?.password, extraData), // Prepare user object for API
                file: imageFile,             // Optional selfie image
                updateImage: !!user.selfie, // Flag to decide if selfie should be updated
                setModified: setModified
            })
        ).then(({payload:{data:{success, code, data}}}) => {
            if (success) {
                if(user?.password){
                    setSecretModal(true);
                    setSecretKey(data?.secret)
                }
                toast.success(t('has_been_updated_successfully'));
                setEmptyFields(false);
            }else errorMsg(code);
        });
    };

    /**
     * Handle to change step
     * @param step
     */
    const handleStepChange = (step) => {
        setActiveStep(step);
    };

    const { handleChange } = HandleChange({
    model: extraData,
    setModel: setExtraData,
    });

    return {
        user,
        setUser,
        FormHandler,
        steps, setSteps,
        activeStep, setActiveStep,
        handleStepChange,
        showUnlinkBtn,
        setModified,
        handleChange,
    };
};
